import { useEffect, useRef, useState } from "react";
import { api, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { normalizeLine, LINE_TYPE_LABELS, PRODUCT_FAMILY_LABELS } from "../lib/fields";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, Sparkles, Loader2, ClipboardPaste, Mail } from "lucide-react";

const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const ACCEPT = ".pdf,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.eml,.msg,.txt";

export function RfqDocuments({ module, itemId, canWrite, onAddLines }) {
  const { t } = useApp();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [extracting, setExtracting] = useState(null);
  const [review, setReview] = useState(null);
  const [selected, setSelected] = useState({});
  const fileRef = useRef(null);

  const load = () => api.get(`/${module}/${itemId}/rfq-documents`).then((r) => setDocs(r.data || [])).catch(() => {});
  useEffect(() => { if (itemId) load(); /* eslint-disable-next-line */ }, [itemId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/${module}/${itemId}/rfq-documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("upload") + " ✓"); load();
    } catch (err) { toast.error(errDetail(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const savePaste = async () => {
    if (!pasteText.trim()) return;
    try {
      await api.post(`/${module}/${itemId}/rfq-paste`, { text: pasteText });
      toast.success(t("upload") + " ✓"); setPasteText(""); setShowPaste(false); load();
    } catch (err) { toast.error(errDetail(err)); }
  };

  const removeDoc = async (id) => {
    if (!window.confirm(t("rfq_confirm_delete"))) return;
    try { await api.delete(`/${module}/${itemId}/rfq-documents/${id}`); load(); }
    catch (err) { toast.error(errDetail(err)); }
  };

  const extract = async (doc) => {
    setExtracting(doc.id);
    try {
      const r = await api.post(`/${module}/${itemId}/rfq-documents/${doc.id}/extract`);
      const lines = (r.data.line_items || []).map(normalizeLine);
      setReview({ ...r.data, line_items: lines, docName: doc.filename });
      setSelected(Object.fromEntries(lines.map((_, i) => [i, true])));
    } catch (err) { toast.error(errDetail(err)); }
    finally { setExtracting(null); }
  };

  const addSelected = () => {
    const picked = review.line_items.filter((_, i) => selected[i]);
    if (!picked.length) { toast.error(t("rfq_none_selected")); return; }
    onAddLines(picked);
    toast.success(t("rfq_added"));
    setReview(null);
  };

  return (
    <div data-testid="rfq-documents">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
          <Mail className="h-4 w-4" />{t("rfq_documents")}
        </h3>
        {canWrite && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile} data-testid="rfq-file-input" />
            <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="rfq-upload-btn">
              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{t("rfq_upload")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowPaste((s) => !s)} data-testid="rfq-paste-toggle">
              <ClipboardPaste className="h-3.5 w-3.5 mr-1" />{t("rfq_paste_email")}
            </Button>
          </div>
        )}
      </div>
      <p className="text-[11px] text-gray-400 mb-2">{t("rfq_upload_hint")}</p>

      {showPaste && canWrite && (
        <div className="mb-3" data-testid="rfq-paste-box">
          <Textarea rows={4} value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder={t("rfq_paste_ph")} className="text-xs" />
          <div className="flex justify-end mt-2">
            <Button size="sm" className="bg-[#1B2A4A] text-white" onClick={savePaste} data-testid="rfq-paste-save">{t("save")}</Button>
          </div>
        </div>
      )}

      {docs.length === 0 ? (
        <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md p-4 text-center" data-testid="rfq-empty">{t("rfq_no_docs")}</div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 border border-gray-100 rounded-md p-2.5" data-testid={`rfq-doc-${d.id}`}>
              {d.kind === "image" ? <ImageIcon className="h-4 w-4 text-gray-400 shrink-0" /> : <FileText className="h-4 w-4 text-gray-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{d.filename}</p>
                <p className="text-[11px] text-gray-400">{fmtSize(d.size)} · {d.uploaded_by} · {(d.uploaded_at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
              {canWrite && (
                <Button size="sm" variant="outline" disabled={extracting === d.id} onClick={() => extract(d)} data-testid={`rfq-extract-${d.id}`}
                  className="border-[#F5A623] text-[#1B2A4A] hover:bg-amber-50">
                  {extracting === d.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1 text-[#F5A623]" />}{t("rfq_extract")}
                </Button>
              )}
              {canWrite && (
                <button className="text-gray-300 hover:text-red-500" onClick={() => removeDoc(d.id)} data-testid={`rfq-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rfq-review-dialog">
          <DialogHeader>
            <DialogTitle>{t("rfq_review_title")}</DialogTitle>
            <DialogDescription>{t("rfq_review_desc")} · {review?.docName}</DialogDescription>
          </DialogHeader>
          {review && (
            <>
              {(review.company || review.contact_person || review.requested_delivery || review.target_price_note) && (
                <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 rounded-md p-3">
                  {review.company && <div><span className="text-gray-400">{t("rfq_meta_company")}: </span>{review.company}</div>}
                  {review.contact_person && <div><span className="text-gray-400">{t("rfq_meta_contact")}: </span>{review.contact_person}</div>}
                  {review.requested_delivery && <div><span className="text-gray-400">{t("rfq_meta_delivery")}: </span>{review.requested_delivery}</div>}
                  {review.target_price_note && <div><span className="text-gray-400">{t("rfq_meta_target")}: </span>{review.target_price_note}</div>}
                </div>
              )}
              {review.line_items.length === 0 && <p className="text-sm text-gray-400 py-4">{t("rfq_no_extract")}</p>}
              <div className="space-y-2 mt-2">
                {review.line_items.map((l, i) => (
                  <div key={i} className="border border-gray-200 rounded-md p-3 flex gap-3" data-testid={`rfq-proposed-${i}`}>
                    <Checkbox checked={!!selected[i]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [i]: !!v }))} data-testid={`rfq-select-${i}`} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <Badge className="bg-[#1B2A4A] text-white hover:bg-[#1B2A4A] text-[10px]">{t(LINE_TYPE_LABELS[l.line_type] || "lt_other")}</Badge>
                        {l.line_type === "Product" && l.product_family && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">{t(PRODUCT_FAMILY_LABELS[l.product_family])}</Badge>}
                        <span className="text-xs font-medium">{l.description}</span>
                      </div>
                      <p className="text-[11px] text-gray-500 font-mono">{Number(l.quantity).toLocaleString("de-DE")} {l.unit}{l.colors ? ` · ${l.colors}C` : ""}{l.unit_price ? ` · ~${l.unit_price}` : ""}</p>
                      {l.source_excerpt && <p className="text-[11px] text-gray-400 italic mt-1 border-l-2 border-gray-200 pl-2">“{l.source_excerpt}”</p>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => setReview(null)}>{t("cancel")}</Button>
                <Button className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" onClick={addSelected} data-testid="rfq-add-selected-btn">{t("rfq_add_selected")}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
